import type {
  CardFormReply,
  MembershipCardDetails,
  MembershipPaymentAction,
} from '../domain/purchase';

/** Card data the form hands over on submit; sent to the backend as is and never logged. */
export interface CardPaymentData {
  paymentMethod: Record<string, unknown>;
  browserInfo: Record<string, unknown> | null;
  origin: string;
}

/** Callbacks the form drives. Each async one always settles, so the form never hangs in "loading". */
export interface CardFormHandlers {
  /** The shopper submitted a valid card. */
  onSubmit(data: CardPaymentData): Promise<CardFormReply>;
  /** A 3DS step finished inside the form (`threeDSResult`). */
  onAdditionalDetails(details: MembershipCardDetails): Promise<CardFormReply>;
  onValidityChange(isValid: boolean): void;
  /** A 3DS challenge became visible to the shopper. */
  onActionShown(): void;
  /** Enter was pressed inside a card field; the form must not submit on its own (the consent gate is the store's). */
  onEnterKey(): void;
  /** The form failed after it was mounted. */
  onError(error: unknown): void;
}

/** Port of the card form; implemented over Adyen and faked in tests. */
export interface CardForm {
  /** Renders the form into `node`; rejects when the payment SDK cannot start. */
  mount(node: HTMLElement, handlers: CardFormHandlers): Promise<void>;
  /** Validates and, when the card is valid, calls `onSubmit`. */
  submit(): void;
  /** Shows a 3DS action that arrived outside a form callback (after a bank redirect). */
  handleAction(action: MembershipPaymentAction): void;
  destroy(): void;
}

/** Creates a fresh, unmounted card form. */
export type CardFormFactory = () => CardForm;

/**
 * The payment SDK's code never arrived (a chunk lost to a deploy or a dropped
 * connection). The browser keeps that failure for the life of the document,
 * so loading it again needs a page reload, not another form.
 */
export class CardFormLoadError extends Error {
  constructor(readonly reason: unknown) {
    super('The payment form code could not be loaded');
    this.name = 'CardFormLoadError';
  }
}
